import { Component, Input, Output, EventEmitter, ViewChild, OnInit } from '@angular/core';
import { NgModel } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import type { GridReadyEvent } from '@ag-grid-community/core';

import { Observable } from 'rxjs';
import { filter, distinctUntilChanged, debounceTime } from 'rxjs/operators';

import { AnalyticsService } from 'rev-shared/analytics/Analytics.Service';
import { IGetInfiniteScrollRows } from 'rev-shared/ui/dataGrid/infiniteScroll/IGetInfiniteScrollRows';
import { IVbUiDataGridColDef } from 'rev-shared/ui/dataGrid/columns/IVbUiDataGridColDef';
import { isString } from 'rev-shared/util';
import { mediumDateValueFormatter } from 'rev-shared/ui/dataGrid/valueFormatters/MediumDateTimeValueFormatter';
import { VbUiInfiniteScrollGridComponent } from 'rev-shared/ui/dataGrid/infiniteScroll/VbUiInfiniteScrollGrid.Component';

import { COLUMN_GROUP } from './ColumnConfig';

import styles from './VideoReports.module.less';

const SEARCH_KEY_LENGTH: number = 2;

@Component({
	selector: 'video-analytics-grid',
	templateUrl: './VideoAnalyticsGrid.Component.html'
})
export class VideoAnalyticsGridComponent implements OnInit {
	@Input() public dataFetchContext: any;
	@Input() public filterText: string;
	@Input() public getRows: IGetInfiniteScrollRows;
	@Input() public videoId: string;

	@ViewChild(VbUiInfiniteScrollGridComponent)
	public grid: VbUiInfiniteScrollGridComponent;
	@ViewChild(NgModel)
	public searchValue: NgModel;
	public searchText$: Observable<string>;
	public columnDefs: IVbUiDataGridColDef[];

	@Output() public gridReady = new EventEmitter<GridReadyEvent>();

	public readonly COLUMN_GROUP = COLUMN_GROUP;
	public readonly mediumDateValueFormatter = mediumDateValueFormatter;
	public readonly pageSize: number = 100;
	public readonly styles = styles;

	constructor(
		public AnalyticsService: AnalyticsService,
		private TranslateService: TranslateService
	) {}

	public ngOnInit(): void {
		this.assignColumnDefs();
	}

	public ngAfterViewInit(): void {
		this.grid.suppressMultiSort = true;
		this.grid.columnPickerEnabled = true;

		this.grid.defaultColDef = {
			...this.grid.defaultColDef,
			minWidth: 100,
			resizable: true
		};

		this.grid.context = {
			...this.grid.context,
			videoId: this.videoId
		};

		this.searchText$ = this.searchValue.valueChanges
			.pipe(
				filter(text => isString(text) && (!text || text.length >= SEARCH_KEY_LENGTH)),
				debounceTime(500),
				distinctUntilChanged()
			);
	}

	private assignColumnDefs(): void {
		this.columnDefs = [
			{
				columnPickerGroup: this.COLUMN_GROUP.SESSION,
				field: 'DateViewed',
				headerName: this.TranslateService.instant('Reports_Column_DateViewed'),
				sortable: true,
				filter: false,
				valueFormatter: this.mediumDateValueFormatter
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.USER,
				field: 'FullName',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_FullName'),
				sortable: true,
				cellRenderer: 'highlightText'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.METRICS,
				field: 'IsComplete',
				headerName: this.TranslateService.instant('Reports_Column_Completed'),
				filter: false,
				sortable: true
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.METRICS,
				field: 'DropOff',
				headerName: this.TranslateService.instant('Reports_Column_DropOff'),
				sortable: true,
				filter: false
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.METRICS,
				field: 'SessionTime',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_ViewingTime'),
				sortable: true,
				filter: false
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.METRICS,
				field: 'PublicCDNTime',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_PublicCDNTime'),
				hide: true,
				sortable: true,
				filter: false
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.METRICS,
				field: 'ECDNTime',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_ECDNTime'),
				hide: true,
				sortable: true,
				filter: false
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.USER,
				field: 'Username',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_UserName'),
				sortable: true,
				hide: true,
				cellRenderer: 'highlightText',
				tooltipField: 'Username'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.USER,
				field: 'FirstName',
				headerName: this.TranslateService.instant('FirstName'),
				hide: true,
				sortable: true,
				cellRenderer: 'highlightText'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.USER,
				field: 'LastName',
				headerName: this.TranslateService.instant('LastName'),
				hide: true,
				sortable: true,
				cellRenderer: 'highlightText'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.USER,
				field: 'Email',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_Email'),
				hide: true,
				sortable: true,
				cellRenderer: 'highlightText'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.USER,
				field: 'UserType',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_UserType'),
				sortable: true,
				hide: true,
				cellRenderer: 'highlightText',
				tooltipField: 'UserType'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.SESSION,
				field: 'ZoneName',
				headerName: this.TranslateService.instant('Reports_Column_ZoneName'),
				hide: true,
				sortable: true,
				tooltipField: 'ZoneName',
				cellRenderer: 'highlightText'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.SESSION,
				field: 'StreamDevice',
				headerName: this.TranslateService.instant('Reports_Column_Device'),
				hide: true,
				sortable: true,
				tooltipField: 'StreamDevice',
				cellRenderer: 'highlightText'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.SESSION,
				field: 'StreamAccessed',
				headerName: this.TranslateService.instant('Reports_Column_PlaybackUrl'),
				hide: true,
				sortable: true,
				tooltipField: 'StreamAccessed',
				cellRenderer: 'highlightText'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.SESSION,
				field: 'ViewContext',
				headerName: this.TranslateService.instant('Reports_Column_ViewContext'),
				hide: true,
				sortable: true,
				tooltipField: 'ViewContext',
				cellRenderer: 'highlightText'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.SESSION,
				field: 'IpAddress',
				headerName: this.TranslateService.instant('IPAddress'),
				hide: true,
				sortable: true,
				cellRenderer: 'highlightText'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.SYSTEM,
				field: 'PlayerDevice',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_DeviceType'),
				hide: true,
				sortable: true,
				cellRenderer: 'highlightText'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.SYSTEM,
				field: 'OsFamily',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_Platform'),
				hide: true,
				sortable: true,
				cellRenderer: 'highlightText'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.SYSTEM,
				field: 'OsName',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_PlatformVersion'),
				hide: true,
				sortable: true,
				cellRenderer: 'highlightText'
			},
			{
				columnPickerGroup: this.COLUMN_GROUP.SYSTEM,
				field: 'Browser',
				headerName: this.TranslateService.instant('Reports_Attendees_Column_Browser'),
				hide: true,
				sortable: true,
				cellRenderer: 'highlightText'
			},
		];
	}
}
